/**
 * Daily Reward sheet (P3-T01): 7-day check-in streak, claimed on the server.
 * Opened from the top-bar gift button and once per day on Home when claimable.
 */
import type { DailyRewardStatus } from '@loomia/shared';
import { api, ApiError } from '../api/client.js';
import { hasAccount } from '../state/auth.js';
import { refreshCredits } from '../state/credits.js';
import { openSignInSheet } from './signinSheet.js';
import { toast } from './toast.js';

const SHOWN_KEY = 'loomia.dailyShown';
let sheet: HTMLElement | null = null;
let timer: number | undefined;

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function fmt(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
}

export function closeDailyReward(): void {
  window.clearInterval(timer);
  sheet?.remove();
  sheet = null;
}

/** Opens the sheet. Guests are asked to sign in first. */
export async function openDailyReward(): Promise<void> {
  if (!hasAccount()) {
    openSignInSheet('Sign in to claim daily credits.');
    return;
  }
  if (sheet) return;
  try {
    localStorage.setItem(SHOWN_KEY, today());
  } catch {
    /* ignore */
  }
  const el = document.createElement('div');
  el.className = 'daily-backdrop';
  el.innerHTML = '<div class="daily-sheet" role="dialog" aria-modal="true" aria-labelledby="dr-title"><p class="muted" style="text-align:center">Loading…</p></div>';
  document.body.appendChild(el);
  sheet = el;
  el.addEventListener('click', (e) => {
    if (e.target === el) closeDailyReward();
  });
  try {
    render(el, await api.getDailyReward());
  } catch (err) {
    closeDailyReward();
    toast(err instanceof ApiError ? err.message : 'Could not load daily rewards. Try again.');
  }
}

/** Auto-open once per local day, only when there is something to claim. */
export async function maybeAutoOpenDailyReward(): Promise<void> {
  if (!hasAccount()) return;
  try {
    if (localStorage.getItem(SHOWN_KEY) === today()) return;
    const st = await api.getDailyReward();
    if (st.canClaim) await openDailyReward();
  } catch {
    /* best effort */
  }
}

/** Whether a reward can be claimed right now (for the badge dot). */
export async function dailyRewardClaimable(): Promise<boolean> {
  if (!hasAccount()) return false;
  try {
    return (await api.getDailyReward()).canClaim;
  } catch {
    return false;
  }
}

function render(el: HTMLElement, st: DailyRewardStatus): void {
  window.clearInterval(timer);
  const n = st.schedule.length;
  const rows = st.schedule
    .slice(0, n - 1)
    .map((credits, i) => {
      const day = i + 1;
      const claimed = day <= st.claimedDays;
      const current = st.canClaim && day === st.nextDay;
      return `<li class="dr-row${claimed ? ' is-claimed' : ''}${current ? ' is-current' : ''}">
        <span class="dr-dot" aria-hidden="true">${claimed ? '✓' : ''}</span>
        <span class="dr-day">Day ${day}</span>
        <span class="dr-amt">+${credits} credits <span class="credit">✦</span></span>
      </li>`;
    })
    .join('');
  const prize = st.schedule[n - 1]!;
  const unlocked = st.canClaim && st.nextDay === n;
  const prizeClaimed = st.claimedDays >= n;
  el.innerHTML = `
    <div class="daily-sheet" role="dialog" aria-modal="true" aria-labelledby="dr-title">
      <button class="daily-x" type="button" aria-label="Close" data-dr="close">✕</button>
      <div class="dr-head">
        <h2 id="dr-title">Daily Reward</h2>
        <p>Keep checking in daily to increase your rewards</p>
      </div>
      <ol class="dr-list">
        ${rows}
        <li class="dr-box${unlocked ? ' is-current' : ''}">
          <span class="dr-gift" aria-hidden="true">🎁</span>
          <span><strong>Mystery gift box</strong><small>${unlocked ? 'Ready — claim to open it!' : 'Reach Day 7 to open'}</small></span>
        </li>
        <li class="dr-prize${prizeClaimed ? ' is-claimed' : ''}${unlocked ? ' is-current' : ''}">
          <span class="dr-lock" aria-hidden="true">${prizeClaimed ? '✓' : unlocked ? '🔓' : '🔒'}</span>
          <span><strong>Day ${n} · Grand Prize</strong><b class="credit">+${prize} credits ✦</b></span>
        </li>
      </ol>
      <button class="btn btn-primary btn-block dr-claim" type="button" data-dr="claim" ${st.canClaim ? '' : 'disabled'}>
        ${st.canClaim ? `Claim +${st.schedule[st.nextDay - 1]} credits` : st.capReached ? 'All daily rewards collected' : ''}
      </button>
    </div>`;

  const claim = el.querySelector<HTMLButtonElement>('[data-dr="claim"]')!;
  if (!st.canClaim && st.nextClaimAt) {
    const at = new Date(st.nextClaimAt).getTime();
    const tick = (): void => {
      const left = at - Date.now();
      if (left <= 0) {
        window.clearInterval(timer);
        void api.getDailyReward().then((s) => sheet === el && render(el, s)).catch(() => undefined);
        return;
      }
      claim.textContent = `Next reward in ${fmt(left)}`;
    };
    tick();
    timer = window.setInterval(tick, 1000);
  }

  el.querySelector('[data-dr="close"]')?.addEventListener('click', closeDailyReward);
  claim.addEventListener('click', () => {
    claim.disabled = true;
    api
      .claimDailyReward()
      .then((res) => {
        void refreshCredits().catch(() => undefined);
        toast(`+${res.credits} credits claimed!`);
        document.dispatchEvent(new CustomEvent('loomia:daily-claimed'));
        if (sheet === el) render(el, res.status);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.code === 'already_claimed') {
          void api.getDailyReward().then((s) => sheet === el && render(el, s)).catch(() => undefined);
        }
        claim.disabled = false;
        toast(err instanceof ApiError ? err.message : 'Could not claim. Try again.');
      });
  });
}
